<template>
  <div id="report-grid" :class="theme" dir="ltr">
    <h-unsaved-changes-dialog
      id="show-modal"
      v-if="showingUnsavedDialog"
      :busy="saving"
      @cancel="setShowingUnsavedDialog(false)"
      @discard="discardChanges"
      @save="saveAndExit">
    </h-unsaved-changes-dialog>
    <h-toast v-if="toastMessage" :message="toastMessage"></h-toast>
    <h-toolbar
      :title="title"
      :subtitle="subtitle"
      :dirty="dirty"
      :save-disabled="savingDisabled"
      :close-disabled="closeDisabled"
      :cut-disabled="editCommandsDisabled"
      :copy-disabled="editCommandsDisabled"
      :paste-disabled="editCommandsDisabled"
      :clear-disabled="editCommandsDisabled"
      :insert-disabled="insertDisabled"
      :can-insert="canInsert"
      :saving="saving"
      @insertrow="insertRow"
      @clear="clear"
      @cut="cut"
      @paste="paste"
      @close="closeEditMode"
      @saveAndExit="saveAndExit"
      @saveAndContinue="saveAndContinue"
      @copy="copy"
    >
    </h-toolbar>
    <h-spinner v-if="loading"></h-spinner>
    <div id="visualization" v-else>
        <!-- the if below, is to make sure the theme is set, otherwise flexgrid stops responding -->
        <!-- if the theme property ever changes after the flexgrid is loaded, we would need to refresh the page -->
        <h-grid v-if="theme"
          componentId="grid"
          :userId="userId"
          :managerId="managerId"
          :items-source="cells"
          :items-metadata="metadata"
          :scroll-position-changed="scrollPositionChanged"
          :editable="editable"
          :cellStyles="cellStyles"
          :language="language"
          :theme="theme"
          :columns="columns"
          :columnHeaderCellStyles="columnHeaderCellStyles"
          @selectionchange="selectionChanged"
          @copy="copy"
          @cut="cut"
      >
        </h-grid>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import osDetection from '@/os-detection'
import {
    extractCells,
    extractCellsMetaData,
    extractIsInReadOnlyMode
} from '@/data-extraction/reports'
import limits from '@/clipboard/limits'
import Grid from './Grid'
import Toolbar from './Toolbar'
import Spinner from '@/components/common/Spinner'
import Toast from '@/components/common/Toast'
import UnsavedChangesDialog from '@/components/grid/UnsavedChangesDialog'
import { EditCell } from '@/data-entry'
import { Reports } from '@/api'
import Clipboard from '@/clipboard/clipboard'
import utils from '@/utils'
import { CONST } from '@/utils/constants'

export default {
  components: {
    'h-grid': Grid,
    'h-toolbar': Toolbar,
    'h-spinner': Spinner,
    'h-unsaved-changes-dialog': UnsavedChangesDialog,
    'h-toast': Toast
  },
  data () {
    return {
      cells: [],
      metadata: [],
      pageSize: CONST.Grid.PageSize,
      selection: null,
      currentRow: null
    }
  },
  computed: {
    ...mapGetters({
      dirty: 'report/dirty',
      saving: 'report/saving',
      loading: 'report/loading', // this indicates if the component is initially loading, it is not set during scroll
      inserting: 'report/inserting',
      total: 'report/total',
      showingUnsavedDialog: 'report/showingUnsavedDialog',
      title: 'report/title',
      subtitle: 'report/subtitle',
      userId: 'user/userId',
      managerId: 'report/managerId',
      language: 'user/language',
      theme: 'user/theme',
      columnHeaderMetaData: 'report/columnHeaderMetaData',
      selectedRange: 'report/selectedRange',
      canInsert: 'report/canInsert',
      columns: 'report/columns',
      editing: 'report/editing'
    }),
    closeDisabled () {
      return this.busy
    },
    savingDisabled () {
      return !this.dirty || this.busy
    },
    insertDisabled () {
      return this.busy
    },
    editCommandsDisabled () {
      return !this.hasSelectedRange || this.busy
    },
    busy () {
      return this.loading || this.saving || this.inserting || this.editing
    },
    hasSelectedRange () {
      return !utils.isNullOrUndefined(this.selectedRange)
    },
    toastMessage () {
      return this.saving && !this.showingUnsavedDialog ? this.$i18n.translate('saving message') : ''
    }
  },
  methods: {
    ...mapActions('report', [
      'setDirty',
      'loadingFinished',
      'setShowingUnsavedDialog',
      'clearCell',
      'clearCells',
      'save',
      'loadReport',
      'insert',
      'addError',
      'setReadOnlyMode',
      'switchToReadOnlyMode',
      'getReport',
      'setSelectedRange',
      'editCell',
      'refreshMetadata',
      'refresh'

    ]),
    scrollPositionChanged: function () {
      this.refreshMetadata()
    },
    injectReportDataIntoCells (report) {
      this.cells = extractCells(this.columns, report)
      this.metadata = extractCellsMetaData(this.columns, report)
    },
    selectionChanged (data, selected) {
      this.setSelectedRange(selected)
      this.updateCurrentRow(selected.selectedRow)
      this.selection = data
    },
    editable (row, col) {
      // checks cell metadata to see if a particular cell is editable
      const editable = this.metadata[row][col].cellType
      return editable === 'Editable'
    },
    cellStyles (row, col) {
      if (this.metadata[row]) {
        if (this.editable(row, col)) {
          return ['wj-hubble-editable', ...this.metadata[row][col].styles]
        }
        return this.metadata[row][col].styles
      }
      return []
    },
    columnHeaderCellStyles (col) {
      return this.columnHeaderMetaData[col].UniqueId
    },
    insertRow () {
      this.insert(this.currentRow ? this.currentRow : 0)
          .then((response) => {
            this.refresh()
              .then(report => {
                this.injectReportDataIntoCells(report)
              })
          })
          .catch(err => this.addError(err))
    },
    updateCurrentRow (rowIndex) {
      this.currentRow = rowIndex
    },
    clear () {
      if (this.hasSelectedRange) {
        this.checkSelectionLimit('clear', () => {
          if (this.selectedRange.isSingleCell) {
            if (this.editable(this.selectedRange.selectedRow, this.selectedRange.selectedCol)) {
              this.clearCell()
              .then((response) => {
                const success = EditCell.getSuccess(response)
                if (!success) {
                  const error = EditCell.getClearCellError(response)
                  this.addError(new Error(error))
                } else {
                  // set value as received from server
                  const newValue = EditCell.getClearCellNewValue(response)
                  const key = `col${this.selectedRange.selectedCol}`
                  this.setCell(this.selectedRange.selectedRow, key, newValue)
                }
              })
              .catch(err => {
                this.addError(err)
              })
            }
          } else {
            const selectedRange = this.selectedRange
            this.clearCells()
            .then((response) => {
              const success = EditCell.getSuccess(response)
              if (!success) {
                const error = EditCell.getClearCellError(response)
                this.addError(new Error(error))
              } else {
                let i = selectedRange.topRow
                const firstCol = Math.min(selectedRange.col, selectedRange.col2)
                const lastCol = Math.max(selectedRange.col, selectedRange.col2)
                for (; i <= selectedRange.bottomRow; i++) {
                  for (let j = firstCol; j <= lastCol; j++) {
                    if (this.editable(i, j)) {
                      // set value as received from server
                      const newValue = ''
                      const key = `col${j}`
                      this.setCell(i, key, newValue)
                    }
                  }
                }
              }
            })
            .catch(err => this.addError(err))
          }
        })
      }
    },
    setCell (i, key, newVal) {
      this.cells[i][key] = newVal
      this.$set(this.cells, i, this.cells[i])
    },
    paste () {
      this.addError(new Error(osDetection.pasteMessage()))
    },
    cut () {
      if (this.hasSelectedRange) {
        this.checkSelectionLimit('cut', () => {
          Clipboard.newClipboard('#cut', this.selection)
          this.clear()
        })
      }
    },
    copy () {
      if (this.hasSelectedRange) {
        this.checkSelectionLimit('copy', () => {
          Clipboard.newClipboard('#copy', this.selection)
        })
      }
    },
    closeEditMode () {
      return this.getReport({ start: 0, count: 1, refresh: true })
        .then((report) => {
          if (!this.dirty || extractIsInReadOnlyMode(report)) {
            return this.switchToReadOnlyMode()
          } else {
            this.setShowingUnsavedDialog(true)
          }
        })
        .catch(err => {
          this.addError(err)
        })
    },
    saveAndContinue () {
      this.save()
      .then((response) => {
        this.refresh()
          .then(report => {
            this.injectReportDataIntoCells(report)
          })
      })
      .catch(err => this.addError(err))
    },
    saveAndExit () {
      this.save()
      .then(() => Reports.switchModes(this.userId, this.managerId))
      .then(() => this.setReadOnlyMode()) // TODO: Use switchToReadOnlyMode, // TODO: Use switchToReadOnlyMode, first confirming the correct way to handle errors when doing this
      .catch(err => {
        this.addError(err)
      })
      .then(() => {
        this.setShowingUnsavedDialog(false)
      })
    },
    discardChanges () {
      this.setShowingUnsavedDialog(false)
      this.switchModesAndClose()
    },
    switchModesAndClose () {
      Reports.switchModes(this.userId, this.managerId)
        .then(() => {
          this.setReadOnlyMode()
        })
        .catch(err => {
          this.addError(err)
        })
    },
    checkSelectionLimit (operationType, func) {
      if (limits.actionWithinRowLimit(this.selectedRange.rowSpan)) {
        func()
      } else {
        const err = utils.bulkEditErrorMessage(operationType)
        this.addError(new Error(err))
      }
    }
  },
  mounted () {
    this.loadReport()
    .then(report => {
      this.injectReportDataIntoCells(report)
    })
    .catch(err => this.addError(err))
    .then(() => this.loadingFinished())
  }
}
</script>

<style scoped>

</style>
